/**
 * PriceWebSocketManager - Manages WebSocket connections to multiple exchanges
 * for real-time price updates in the header statistics bar.
 *
 * CRITICAL ARCHITECTURE:
 * - WebSocket onmessage → Update internal state (NO React setState)
 * - Ticker interval (75ms) → Read state → Update React state
 * - This batches renders and prevents performance issues
 *
 * Exchanges:
 * - Binance: BTC/USDT price (text WebSocket)
 * - Upbit: BTC/KRW and USDT/KRW prices (binary WebSocket)
 * - Bithumb: BTC/KRW and USDT/KRW prices (text WebSocket)
 *
 * ## KRW/USDT Conversion Rate
 *
 * To convert KRW prices to USDT, use:
 *   usdtKrw = upbitBtcKrw / binanceBtcUsdt
 *
 * Example:
 *   upbitBtcKrw = 140,000,000 KRW
 *   binanceBtcUsdt = 100,000 USDT
 *   usdtKrw = 1,400 KRW per USDT
 *
 * Then to convert any KRW price to USDT:
 *   priceInUsdt = priceInKrw / usdtKrw
 *
 * Usage: Access via usePriceUpdates() hook which returns HeaderPrices.
 * See: NewPositionDataContext for example of calculating conversion rates.
 */

export type HeaderPrices = {
  binanceBtcUsdt: number | null;   // BTC/USDT from Binance
  upbitBtcKrw: number | null;      // BTC/KRW from Upbit
  bithumbBtcKrw: number | null;    // BTC/KRW from Bithumb
  upbitUsdtKrw: number | null;     // USDT/KRW from Upbit (for Story 4.7)
  bithumbUsdtKrw: number | null;   // USDT/KRW from Bithumb (for Story 4.7)
  officialUsdKrw: number | null;   // Official USD/KRW from Naver Finance
  lastUpdate: number;
};

interface BinanceTradeMessage {
  e: string;  // Event type: "trade"
  s: string;  // Symbol: "BTCUSDT"
  p: string;  // Price (string - must parseFloat)
}

interface UpbitSimpleMessage {
  ty: string;   // Type: "trade"
  cd: string;   // Code: "KRW-BTC" or "KRW-USDT"
  tp: number;   // Trade price (number)
}

interface BithumbSimpleMessage {
  ty: string;   // Type: "trade" or "transaction"
  cd: string;   // Code: "KRW-BTC" or "KRW-USDT"
  tp: number;   // Trade price (number)
}

interface BithumbMaintenanceResponse {
  status?: number | string;
  data?: {
    maintenanceEndAt?: string;
  };
}

export class PriceWebSocketManager {
  private binanceWs: WebSocket | null = null;
  private upbitWs: WebSocket | null = null;
  private bithumbWs: WebSocket | null = null;

  // Fixed struct - NO Map needed! Type-safe and efficient
  private prices: HeaderPrices = {
    binanceBtcUsdt: null,
    upbitBtcKrw: null,
    bithumbBtcKrw: null,
    upbitUsdtKrw: null,
    bithumbUsdtKrw: null,
    officialUsdKrw: null,
    lastUpdate: 0,
  };

  // Naver Finance API polling interval
  private naverPollInterval: ReturnType<typeof setInterval> | null = null;
  private readonly NAVER_POLL_INTERVAL_MS = 60000; // Poll every 60 seconds
  private readonly BITHUMB_MAINTENANCE_URL = 'https://api.bithumb.com/public/ticker/BTC_KRW';
  private readonly BITHUMB_MAINTENANCE_GRACE_MS = 30000;
  private bithumbMaintenanceUntil: number | null = null;
  private bithumbMaintenanceCheck: Promise<number | null> | null = null;

  // Reconnection state
  private reconnectDelays: { [key: string]: number } = {
    binance: 1000,
    upbit: 1000,
    bithumb: 1000,
  };
  private maxReconnectDelay = 60000; // 60 seconds max
  private reconnectTimers: { [key: string]: ReturnType<typeof setTimeout> | null } = {
    binance: null,
    upbit: null,
    bithumb: null,
  };

  private async parseSocketMessage<T>(raw: unknown): Promise<T> {
    if (typeof raw === 'string') {
      return JSON.parse(raw) as T;
    }

    if (raw && typeof raw === 'object' && 'text' in raw && typeof raw.text === 'function') {
      const text = await raw.text();
      return JSON.parse(text) as T;
    }

    return JSON.parse(String(raw)) as T;
  }

  /**
   * Connect to Binance WebSocket for BTC/USDT trade stream
   * Endpoint: wss://stream.binance.com:9443/ws
   * Stream: btcusdt@trade
   */
  connectBinance(): void {
    if (this.binanceWs) {
      console.log('[PriceWS Binance] Already connected');
      return;
    }

    console.log('[PriceWS Binance] Connecting to Binance trade stream...');

    try {
      this.binanceWs = new WebSocket('wss://stream.binance.com:9443/ws');

      // CRITICAL: Use onmessage ONLY, not addEventListener!
      this.binanceWs.onopen = () => {
        console.log('[PriceWS Binance] Connected');
        this.reconnectDelays.binance = 1000; // Reset delay on success

        // Subscribe to BTC/USDT trade stream
        const subscription = {
          method: 'SUBSCRIBE',
          params: ['btcusdt@trade'],
          id: 1,
        };

        console.log('[PriceWS Binance] Subscribing:', JSON.stringify(subscription));
        this.binanceWs?.send(JSON.stringify(subscription));
      };

      this.binanceWs.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data) as BinanceTradeMessage;

          // Only process trade events
          if (data.e === 'trade' && data.s === 'BTCUSDT') {
            const price = parseFloat(data.p); // CRITICAL: Price is string, convert to number
            this.updatePrice('Binance', data.s, price);
          }
        } catch (error) {
          console.error('[PriceWS Binance] Parse error:', error);
        }
      };

      this.binanceWs.onerror = (event: Event) => {
        console.error('[PriceWS Binance] WebSocket error:', event);
      };

      this.binanceWs.onclose = (event: CloseEvent) => {
        console.log('[PriceWS Binance] Disconnected:', event.code, event.reason);
        this.binanceWs = null;
        this.clearPricesForExchange('Binance');
        this.scheduleReconnect('binance', () => this.connectBinance());
      };
    } catch (error) {
      console.error('[PriceWS Binance] Connection error:', error);
      this.scheduleReconnect('binance', () => this.connectBinance());
    }
  }

  /**
   * Connect to Upbit WebSocket for KRW-BTC and KRW-USDT trade tickers
   * Endpoint: wss://api.upbit.com/websocket/v1
   * CRITICAL: Upbit sends BINARY frames - must decode Blob to text first!
   */
  connectUpbit(): void {
    if (this.upbitWs) {
      console.log('[PriceWS Upbit] Already connected');
      return;
    }

    console.log('[PriceWS Upbit] Connecting to Upbit trade stream...');

    try {
      this.upbitWs = new WebSocket('wss://api.upbit.com/websocket/v1');

      this.upbitWs.onopen = () => {
        console.log('[PriceWS Upbit] Connected');
        this.reconnectDelays.upbit = 1000; // Reset delay on success

        // Subscribe to KRW-BTC and KRW-USDT with SIMPLE format
        const subscription = [
          { ticket: 'header' },
          { type: 'trade', codes: ['KRW-BTC', 'KRW-USDT'] },
          { format: 'SIMPLE' },
        ];

        console.log('[PriceWS Upbit] Subscribing:', JSON.stringify(subscription));
        this.upbitWs?.send(JSON.stringify(subscription));
      };

      this.upbitWs.onmessage = async (event: MessageEvent) => {
        try {
          const data = await this.parseSocketMessage<UpbitSimpleMessage>(event.data);

          // Only process trade events
          if (data.ty === 'trade' && (data.cd === 'KRW-BTC' || data.cd === 'KRW-USDT')) {
            this.updatePrice('Upbit', data.cd, data.tp);
          }
        } catch (error) {
          console.error('[PriceWS Upbit] Parse error:', error);
        }
      };

      this.upbitWs.onerror = (event: Event) => {
        console.error('[PriceWS Upbit] WebSocket error:', event);
      };

      this.upbitWs.onclose = (event: CloseEvent) => {
        console.log('[PriceWS Upbit] Disconnected:', event.code, event.reason);
        this.upbitWs = null;
        this.clearPricesForExchange('Upbit');
        this.scheduleReconnect('upbit', () => this.connectUpbit());
      };
    } catch (error) {
      console.error('[PriceWS Upbit] Connection error:', error);
      this.scheduleReconnect('upbit', () => this.connectUpbit());
    }
  }

  /**
   * Connect to Bithumb WebSocket for KRW-BTC and KRW-USDT trade stream
   * Endpoint: wss://ws-api.bithumb.com/websocket/v1
   * Format: [{ ticket }, { type: 'trade', codes: [...] }, { format: 'SIMPLE' }]
   */
  connectBithumb(): void {
    if (this.bithumbWs) {
      console.log('[PriceWS Bithumb] Already connected');
      return;
    }

    if (this.bithumbMaintenanceUntil && Date.now() < this.bithumbMaintenanceUntil) {
      this.scheduleBithumbMaintenanceReconnect(this.bithumbMaintenanceUntil);
      return;
    }

    console.log('[PriceWS Bithumb] Connecting to Bithumb trade stream...');

    try {
      this.bithumbWs = new WebSocket('wss://ws-api.bithumb.com/websocket/v1');

      this.bithumbWs.onopen = () => {
        console.log('[PriceWS Bithumb] Connected');
        this.reconnectDelays.bithumb = 1000; // Reset delay on success

        // Subscribe to trade stream with SIMPLE format
        const subscription = [
          { ticket: 'header_bithumb' },
          {
            type: 'trade',
            codes: ['KRW-BTC', 'KRW-USDT']
          },
          { format: 'SIMPLE' },
        ];

        console.log('[PriceWS Bithumb] Subscribing:', JSON.stringify(subscription));
        this.bithumbWs?.send(JSON.stringify(subscription));
      };

      this.bithumbWs.onmessage = async (event: MessageEvent) => {
        try {
          const data = await this.parseSocketMessage<BithumbSimpleMessage>(event.data);

          // Process trade events (ty = "trade")
          // Use trade price (tp field) as the current price
          if (data.ty === 'trade' && (data.cd === 'KRW-BTC' || data.cd === 'KRW-USDT')) {
            const price = data.tp; // Trade price
            this.updatePrice('Bithumb', data.cd, price);
          }
        } catch (error) {
          console.error('[PriceWS Bithumb] Parse error:', error);
        }
      };

      this.bithumbWs.onerror = (event: Event) => {
        console.error('[PriceWS Bithumb] WebSocket error:', event);
      };

      this.bithumbWs.onclose = (event: CloseEvent) => {
        console.log('[PriceWS Bithumb] Disconnected:', event.code, event.reason);
        this.bithumbWs = null;
        this.clearPricesForExchange('Bithumb');
        void this.handleBithumbDisconnect(event);
      };
    } catch (error) {
      console.error('[PriceWS Bithumb] Connection error:', error);
      void this.handleBithumbDisconnect();
    }
  }

  private async handleBithumbDisconnect(event?: CloseEvent): Promise<void> {
    if (this.bithumbMaintenanceUntil && Date.now() < this.bithumbMaintenanceUntil) {
      this.scheduleBithumbMaintenanceReconnect(this.bithumbMaintenanceUntil);
      return;
    }

    if (event?.code === 1002) {
      const maintenanceUntil = await this.checkBithumbMaintenance();
      if (maintenanceUntil && Date.now() < maintenanceUntil) {
        this.scheduleBithumbMaintenanceReconnect(maintenanceUntil);
        return;
      }
    }

    this.scheduleReconnect('bithumb', () => this.connectBithumb());
  }

  private async checkBithumbMaintenance(): Promise<number | null> {
    if (this.bithumbMaintenanceCheck) {
      return this.bithumbMaintenanceCheck;
    }

    this.bithumbMaintenanceCheck = (async () => {
      try {
        const response = await fetch(this.BITHUMB_MAINTENANCE_URL);
        if (!response.ok) {
          return null;
        }

        const data = await response.json() as BithumbMaintenanceResponse;
        if (String(data?.status) !== '999') {
          this.bithumbMaintenanceUntil = null;
          return null;
        }

        const maintenanceEndAt = data?.data?.maintenanceEndAt;
        if (typeof maintenanceEndAt !== 'string') {
          return null;
        }

        const maintenanceUntil = this.parseBithumbMaintenanceTimestamp(maintenanceEndAt);
        if (maintenanceUntil && maintenanceUntil > Date.now()) {
          this.bithumbMaintenanceUntil = maintenanceUntil;
          return maintenanceUntil;
        }
      } catch (error) {
        console.warn('[PriceWS Bithumb] Maintenance check failed:', error);
      } finally {
        this.bithumbMaintenanceCheck = null;
      }

      return null;
    })();

    return this.bithumbMaintenanceCheck;
  }

  private parseBithumbMaintenanceTimestamp(value: string): number | null {
    const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(value);
    if (!match) {
      return null;
    }

    const [, year, month, day, hour, minute] = match;
    const utcTimestamp = Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour) - 9,
      Number(minute),
      0,
      0
    );

    return Number.isNaN(utcTimestamp) ? null : utcTimestamp;
  }

  private scheduleBithumbMaintenanceReconnect(maintenanceUntil: number): void {
    if (this.reconnectTimers.bithumb) {
      clearTimeout(this.reconnectTimers.bithumb!);
    }

    const delay = Math.max(
      1000,
      maintenanceUntil - Date.now() + this.BITHUMB_MAINTENANCE_GRACE_MS
    );

    const endTime = new Date(maintenanceUntil).toLocaleString('sv-SE', {
      timeZone: 'Asia/Seoul',
      hour12: false,
    });

    console.warn(
      `[PriceWS Bithumb] Maintenance detected until ${endTime} KST. Reconnecting in ${delay}ms...`
    );

    this.reconnectDelays.bithumb = 1000;
    this.reconnectTimers.bithumb = setTimeout(() => {
      this.reconnectTimers.bithumb = null;
      this.bithumbMaintenanceUntil = null;
      this.connectBithumb();
    }, delay);
  }

  /**
   * Update price using last-letter check for efficiency
   * SIMPLIFIED: Check last letter instead of full symbol parsing
   * 'C' = BTC, 'T' = USDT
   */
  private updatePrice(exchange: string, symbol: string, price: number): void {
    const lastChar = symbol[symbol.length - 1]; // Last letter check

    if (exchange === 'Binance') {
      this.prices.binanceBtcUsdt = price; // Binance only sends BTCUSDT
    } else if (lastChar === 'C') {
      // KRW-BTC (last char is 'C')
      if (exchange === 'Upbit') {
        this.prices.upbitBtcKrw = price;
      } else if (exchange === 'Bithumb') {
        this.prices.bithumbBtcKrw = price;
      }
    } else if (lastChar === 'T') {
      // KRW-USDT (last char is 'T')
      if (exchange === 'Upbit') {
        this.prices.upbitUsdtKrw = price;
      } else if (exchange === 'Bithumb') {
        this.prices.bithumbUsdtKrw = price;
      }
    }

    this.prices.lastUpdate = Date.now();
  }

  /**
   * Get current prices - called by ticker to update UI
   * Returns a copy to prevent external mutation
   */
  getPrices(): HeaderPrices {
    return { ...this.prices };
  }

  /**
   * Clear prices for a specific exchange on disconnect
   * Prevents stale data from being displayed
   */
  private clearPricesForExchange(exchange: string): void {
    if (exchange === 'Binance') {
      this.prices.binanceBtcUsdt = null;
    } else if (exchange === 'Upbit') {
      this.prices.upbitBtcKrw = null;
      this.prices.upbitUsdtKrw = null;
    } else if (exchange === 'Bithumb') {
      this.prices.bithumbBtcKrw = null;
      this.prices.bithumbUsdtKrw = null;
    }
    this.prices.lastUpdate = Date.now();
  }

  /**
   * Schedule reconnection with exponential backoff
   * Starts at 1s, doubles each attempt, max 60s
   */
  private scheduleReconnect(exchange: string, reconnectFn: () => void): void {
    // Clear any existing timer
    if (this.reconnectTimers[exchange]) {
      clearTimeout(this.reconnectTimers[exchange]!);
    }

    const delay = this.reconnectDelays[exchange];
    console.log(`[PriceWS ${exchange}] Reconnecting in ${delay}ms...`);

    this.reconnectTimers[exchange] = setTimeout(() => {
      this.reconnectTimers[exchange] = null;

      // Double the delay for next time, up to max
      this.reconnectDelays[exchange] = Math.min(
        this.reconnectDelays[exchange] * 2,
        this.maxReconnectDelay
      );

      reconnectFn();
    }, delay);
  }

  /**
   * Fetch official USD/KRW exchange rate from Naver Finance
   * Uses KEB Hana Bank rate (고시환율)
   */
  async fetchNaverUsdKrw(): Promise<void> {
    try {
      const url = 'https://m.search.naver.com/p/csearch/content/qapirender.nhn?key=calculator&pkid=141&q=%ED%99%98%EC%9C%A8&where=m&u1=keb&u6=standardUnit&u7=0&u3=USD&u4=KRW&u8=down&u2=1';

      const response = await fetch(url);
      if (!response.ok) {
        console.error('[PriceWS Naver] HTTP error:', response.status);
        return;
      }

      const data = await response.json();

      // Extract exchange rate from Naver API response
      // The response structure: data.country[1].value contains the KRW rate
      // country[0] = USD (1), country[1] = KRW (1,436)
      if (data && data.country && data.country[1] && data.country[1].value) {
        const rate = parseFloat(data.country[1].value.replace(/,/g, ''));
        if (!isNaN(rate) && rate > 0) {
          this.prices.officialUsdKrw = rate;
          this.prices.lastUpdate = Date.now();
          console.log('[PriceWS Naver] Updated USD/KRW:', rate);
        }
      }
    } catch (error) {
      console.error('[PriceWS Naver] Fetch error:', error);
    }
  }

  /**
   * Start polling Naver Finance for USD/KRW rate
   */
  startNaverPolling(): void {
    // Fetch immediately
    this.fetchNaverUsdKrw();

    // Then poll every 60 seconds
    this.naverPollInterval = setInterval(() => {
      this.fetchNaverUsdKrw();
    }, this.NAVER_POLL_INTERVAL_MS);

    console.log('[PriceWS Naver] Started polling (interval: 60s)');
  }

  /**
   * Disconnect all WebSocket connections and clear timers
   */
  disconnect(): void {
    console.log('[PriceWS] Disconnecting all exchanges...');

    // Clear reconnect timers
    Object.keys(this.reconnectTimers).forEach((key) => {
      if (this.reconnectTimers[key]) {
        clearTimeout(this.reconnectTimers[key]!);
        this.reconnectTimers[key] = null;
      }
    });

    // Clear Naver polling interval
    if (this.naverPollInterval) {
      clearInterval(this.naverPollInterval);
      this.naverPollInterval = null;
    }

    // Close WebSocket connections
    if (this.binanceWs) {
      this.binanceWs.close();
      this.binanceWs = null;
    }
    if (this.upbitWs) {
      this.upbitWs.close();
      this.upbitWs = null;
    }
    if (this.bithumbWs) {
      this.bithumbWs.close();
      this.bithumbWs = null;
    }

    // Clear all prices
    this.prices = {
      binanceBtcUsdt: null,
      upbitBtcKrw: null,
      bithumbBtcKrw: null,
      upbitUsdtKrw: null,
      bithumbUsdtKrw: null,
      officialUsdKrw: null,
      lastUpdate: 0,
    };

    console.log('[PriceWS] All exchanges disconnected');
  }
}
